import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import { useLanguage } from '../components/LanguageContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Mail, Lock, User, LogIn, UserPlus, Chrome } from 'lucide-react';

export default function Login() {
  const { language, isRTL } = useLanguage();
  const { login, register, loginWithGoogle, user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // If already logged in, redirect to dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  const [mode, setMode] = useState(() =>
    // If a referral code is in the URL → start in register mode automatically
    (searchParams.get('ref') || searchParams.get('referral') || searchParams.get('referral_code')) ? 'register' : 'login'
  );
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState(() =>
    searchParams.get('ref') || searchParams.get('referral') || searchParams.get('referral_code') || ''
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // ── Bot protection ───────────────────────────────────────────────────────
  // 1. Honeypot — invisible field that real users never fill
  const [honeypot, setHoneypot] = useState('');
  // 2. Math captcha — simple "what is 3 + 7?" — much easier than reCAPTCHA setup
  //    and effective against >99% of dumb bots that scrape forms
  const [captcha] = useState(() => ({
    a: Math.floor(Math.random() * 9) + 1,
    b: Math.floor(Math.random() * 9) + 1,
  }));
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  // 3. Form-render timestamp — block submissions faster than 3s (bots fill instantly)
  const [formRenderedAt] = useState(() => Date.now());
  // 4. Client-side rate limit — block more than 3 registrations from same browser per hour
  const checkClientRateLimit = () => {
    try {
      const key = 'pb_signup_attempts';
      const now = Date.now();
      const list = JSON.parse(localStorage.getItem(key) || '[]')
        .filter((ts) => now - ts < 3600000); // keep last hour
      if (list.length >= 3) return false;
      list.push(now);
      localStorage.setItem(key, JSON.stringify(list));
      return true;
    } catch { return true; }
  };

  const t = {
    he: {
      title_login: 'התחברות',
      title_register: 'הרשמה',
      name: 'שם מלא',
      email: 'אימייל',
      password: 'סיסמה',
      login_btn: 'התחבר',
      register_btn: 'הרשם',
      google_btn: 'המשך עם Google',
      no_account: 'אין לך חשבון? הירשם',
      have_account: 'כבר יש חשבון? התחבר',
      login_success: 'התחברת בהצלחה! 👋',
      register_success: 'נרשמת בהצלחה! קיבלת 5 קרדיטים 🎉',
      google_success: 'התחברת עם Google בהצלחה! 🎉',
      error_generic: 'שגיאה, נסי שוב',
      subtitle_login: 'ברוך שובך לעמדת הצילום',
      subtitle_register: 'הצטרף וקבל 5 קרדיטים בחינם',
      reset_password: 'שכחת סיסמה?',
      name_placeholder: 'השם שלך',
      email_placeholder: 'your@email.com',
      password_placeholder: '••••••••',
    },
    en: {
      title_login: 'Login',
      title_register: 'Register',
      name: 'Full Name',
      email: 'Email',
      password: 'Password',
      login_btn: 'Login',
      register_btn: 'Register',
      google_btn: 'Continue with Google',
      no_account: "Don't have an account? Register",
      have_account: 'Already have an account? Login',
      login_success: 'Logged in successfully! 👋',
      register_success: 'Registered successfully! You got 5 free credits 🎉',
      google_success: 'Signed in with Google! 🎉',
      error_generic: 'An error occurred, please try again',
      subtitle_login: 'Welcome back to the photo booth',
      subtitle_register: 'Join and get 5 free credits',
      reset_password: 'Forgot password?',
      name_placeholder: 'Your name',
      email_placeholder: 'your@email.com',
      password_placeholder: '••••••••',
    },
  }[language] || { title_login: 'Login' };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ── Bot protection (only on register) ────────────────────────────────
    if (mode === 'register') {
      // 1. Honeypot
      if (honeypot.trim()) {
        console.warn('Bot detected: honeypot filled');
        toast.error(language === 'he' ? 'שגיאה. נסה שוב מאוחר יותר.' : 'Error. Try again later.');
        return;
      }
      // 2. Form too fast
      if (Date.now() - formRenderedAt < 3000) {
        toast.error(language === 'he' ? 'נא להמתין רגע ולנסות שוב.' : 'Please wait a moment and try again.');
        return;
      }
      // 3. Math captcha
      if (parseInt(captchaAnswer, 10) !== captcha.a + captcha.b) {
        toast.error(language === 'he' ? 'תשובת האימות שגויה. נסה שוב.' : 'Wrong captcha answer. Try again.');
        return;
      }
      // 4. Client-side rate limit
      if (!checkClientRateLimit()) {
        toast.error(language === 'he'
          ? 'נרשמת יותר מדי פעמים. נסה שוב בעוד שעה.'
          : 'Too many signups. Try again in an hour.');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (mode === 'login') {
        await login({ email, password });
        toast.success(t.login_success);
      } else {
        const code = (referralCode || '').trim();
        await register({ name, email, password, referral_code: code || undefined });
        toast.success(code ? t.register_success_referral || t.register_success : t.register_success);
      }
      navigate('/dashboard');
    } catch (err) {
      console.error('Auth error:', err.code, err.message);
      const msg = getFirebaseErrorMessage(err.code, err.message, language);
      toast.error(msg, { duration: 6000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      // Stash any manually-entered referral code so AuthContext can pick it up
      // after the Google popup redirect (it falls back to sessionStorage).
      const code = (referralCode || '').trim();
      if (code) {
        try { sessionStorage.setItem('pending_referral_code', code); } catch { /* ignore */ }
      }
      await loginWithGoogle();
      toast.success(t.google_success);
      navigate('/dashboard');
    } catch (err) {
      console.error('Google auth error:', err.code, err.message);
      if (err.code !== 'auth/popup-closed-by-user') {
        const msg = getFirebaseErrorMessage(err.code, err.message, language);
        toast.error(msg, { duration: 6000 });
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center bg-slate-950 px-4 ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-pink-600/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md z-10"
      >
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-pink-600 mb-4 shadow-lg shadow-violet-500/30">
            <span className="text-2xl">📸</span>
          </div>
          <h1 className="text-2xl font-bold text-white">AI Photo Booth</h1>
          <p className="text-slate-400 mt-1 text-sm">
            {mode === 'login' ? t.subtitle_login : t.subtitle_register}
          </p>
        </div>

        <Card className="bg-slate-900/80 backdrop-blur border-slate-800 shadow-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-center text-xl">
              {mode === 'login' ? t.title_login : t.title_register}
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-5">
            {/* Google Sign-In */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              disabled={googleLoading || isSubmitting}
              className="w-full border-slate-700 bg-slate-800/50 text-white hover:bg-slate-700 flex items-center justify-center gap-3 h-11"
            >
              {googleLoading ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M23.745 12.27c0-.79-.07-1.54-.19-2.27h-11.3v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
                  <path fill="#34A853" d="M12.255 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96h-3.98v3.09C3.515 21.3 7.615 24 12.255 24z" />
                  <path fill="#FBBC05" d="M5.525 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62h-3.98a11.86 11.86 0 000 10.76l3.98-3.09z" />
                  <path fill="#EA4335" d="M12.255 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C18.205 1.19 15.495 0 12.255 0c-4.64 0-8.74 2.7-10.71 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z" />
                </svg>
              )}
              {t.google_btn}
            </Button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-800" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-slate-900 px-3 text-slate-500">
                  {language === 'he' ? 'או עם אימייל' : 'or with email'}
                </span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">{t.name}</Label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t.name_placeholder}
                      className="bg-slate-950 border-slate-800 text-white pl-10"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">{t.email}</Label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    placeholder={t.email_placeholder}
                    className="bg-slate-950 border-slate-800 text-white pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">{t.password}</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type="password"
                    placeholder={t.password_placeholder}
                    className="bg-slate-950 border-slate-800 text-white pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              {/* Honeypot — invisible to humans, only bots fill it */}
              {mode === 'register' && (
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                  aria-hidden="true"
                />
              )}

              {/* Referral code (register only — optional) */}
              {mode === 'register' && (
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    🎁 {language === 'he' ? 'קוד הפניה (לא חובה)' : 'Referral code (optional)'}
                  </Label>
                  <Input
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    type="text"
                    placeholder={language === 'he' ? 'REF000ABC12 — אם חבר שלח לך' : 'REF000ABC12 — if a friend sent you'}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                    autoComplete="off"
                  />
                  {referralCode && (
                    <p className="text-xs text-green-400">
                      {language === 'he'
                        ? '✓ אתה והחבר שהזמין אותך תקבלו בונוס קרדיטים בעת הרכישה הראשונה'
                        : '✓ You and the friend who invited you will get bonus credits on your first purchase'}
                    </p>
                  )}
                </div>
              )}

              {/* Math captcha (register only) */}
              {mode === 'register' && (
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">
                    {language === 'he'
                      ? `אימות אנושי: כמה זה ${captcha.a} + ${captcha.b}?`
                      : `Human check: what is ${captcha.a} + ${captcha.b}?`}
                  </Label>
                  <Input
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    type="number"
                    placeholder="?"
                    className="bg-slate-950 border-slate-800 text-white"
                    required
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting || googleLoading}
                className="w-full bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 h-11 font-semibold"
              >
                {isSubmitting ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : mode === 'login' ? (
                  <><LogIn className="w-4 h-4 mr-2" />{t.login_btn}</>
                ) : (
                  <><UserPlus className="w-4 h-4 mr-2" />{t.register_btn}</>
                )}
              </Button>
            </form>

            {/* Toggle mode */}
            <div className="text-center text-sm">
              <button
                type="button"
                className="text-violet-400 hover:text-violet-300 transition-colors"
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              >
                {mode === 'login' ? t.no_account : t.have_account}
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// Map Firebase error codes to user-friendly messages
function getFirebaseErrorMessage(code, rawMessage, language) {
  // Special case: Firebase Auth service not enabled
  if (code === 'auth/configuration-not-found' || (rawMessage && rawMessage.includes('CONFIGURATION_NOT_FOUND'))) {
    return language === 'he'
      ? 'שירות ההתחברות לא הופעל בFirebase. יש לפתוח Firebase Console ולהפעיל Authentication עם Email/Password.'
      : 'Firebase Authentication is not enabled. Please go to Firebase Console → Authentication → Sign-in method and enable Email/Password.';
  }

  const he = {
    'auth/user-not-found': 'משתמש לא נמצא',
    'auth/wrong-password': 'סיסמאה שגויא',
    'auth/email-already-in-use': 'האימייל כבר קיים במערכת',
    'auth/invalid-email': 'כתובת אימייל לא תקינה',
    'auth/weak-password': 'הסיסמאה חייבת להכיל לפחות 6 תווים',
    'auth/network-request-failed': 'בעיית תקשורת, בדוק את החיבור לאינטרנט',
    'auth/too-many-requests': 'יותר מדי ניסיונות. נסה שוב מאוחר יותר',
    'auth/invalid-credential': 'פרטי ההתחברות שגויים',
    'auth/popup-blocked': 'הדפדפן חסם את חלון Google. אפשר חלונות קפיצה בדפדפן ונסה שוב.',
    'auth/operation-not-allowed': 'שיטת התחברות זו אינה מופעלת. יש להפעיל אותה ב-Firebase Console.',
    'auth/unauthorized-domain': 'הדומיין הזה לא מורשה ב-Firebase. יש להוסיף אותו ב-Firebase Console תחת Authentication -> Settings -> Authorized domains.',
  };
  const en = {
    'auth/user-not-found': 'User not found',
    'auth/wrong-password': 'Wrong password',
    'auth/email-already-in-use': 'Email already in use',
    'auth/invalid-email': 'Invalid email address',
    'auth/weak-password': 'Password must be at least 6 characters',
    'auth/network-request-failed': 'Network error, check your connection',
    'auth/too-many-requests': 'Too many attempts. Try again later',
    'auth/invalid-credential': 'Invalid credentials',
    'auth/popup-blocked': 'Popup blocked by browser. Allow popups and try again.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled. Enable it in Firebase Console.',
    'auth/unauthorized-domain': 'This domain is not authorized for Google Sign-In. Add it in Firebase Console Authentication settings.',
  };
  
  const dict = language === 'he' ? he : en;
  return dict[code] || (rawMessage ? `${language === 'he' ? 'שגיאה התרחשה:' : 'Error occurred:'} ${rawMessage}` : (language === 'he' ? 'שגיאה לא ידועה במערכת ההתחברות' : 'Unknown authentication error'));
}
